import { forwardRef } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { Search } from "lucide-react";
import { cn } from "../lib/cn";

export const inputVariants = cva(
  [
    "w-full appearance-none rounded-input border bg-surface text-foreground",
    "placeholder:text-subtle-foreground",
    "transition-[border-color,box-shadow,background-color] duration-[var(--duration-micro)]",
    // foco: borda violeta + anel de 3px (DESIGN.md › Components › input)
    "focus-visible:outline-none focus-visible:border-primary focus-visible:ring-3 focus-visible:ring-ring/35",
    "disabled:cursor-not-allowed disabled:bg-muted disabled:text-subtle-foreground",
    "max-md:min-h-11",
  ],
  {
    variants: {
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-3.5 text-sm",
        lg: "h-11 px-3.5 text-base",
      },
      invalid: {
        true: "border-danger focus-visible:border-danger focus-visible:ring-danger/25",
        false: "border-border hover:enabled:border-border-strong",
      },
      /** Busca: cápsula cinza sem borda (busca global / busca em tabela). */
      search: {
        true: "rounded-pill border-transparent bg-muted hover:enabled:border-transparent hover:enabled:bg-grey-150 focus-visible:bg-surface",
        false: "",
      },
      /** `true` quando há ícone à esquerda — abre espaço. */
      withLeading: { true: "pl-9.5", false: "" },
      withTrailing: { true: "pr-9.5", false: "" },
    },
    defaultVariants: {
      size: "md",
      invalid: false,
      search: false,
      withLeading: false,
      withTrailing: false,
    },
  },
);

export type InputSize = NonNullable<VariantProps<typeof inputVariants>["size"]>;

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size">,
    Pick<VariantProps<typeof inputVariants>, "size"> {
  invalid?: boolean;
  /** Ícone decorativo à esquerda (busca, etc). */
  leadingIcon?: ReactNode;
  /** Ícone/ação à direita. */
  trailingIcon?: ReactNode;
  /** Número/valor: liga tabular-nums e alinhamento à direita. */
  numeric?: boolean;
  /** Cápsula cinza com lupa. A lupa entra sozinha se não houver `leadingIcon`. */
  search?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    className,
    size,
    invalid = false,
    leadingIcon,
    trailingIcon,
    numeric = false,
    search = false,
    ...props
  },
  ref,
) {
  const lead = leadingIcon ?? (search ? <Search /> : null);

  const field = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        inputVariants({
          size,
          invalid,
          search,
          withLeading: Boolean(lead),
          withTrailing: Boolean(trailingIcon),
        }),
        numeric && "nums text-right",
        className,
      )}
      {...props}
    />
  );

  if (!lead && !trailingIcon) return field;

  return (
    <div className="relative w-full">
      {lead ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-subtle-foreground [&_svg]:size-4"
        >
          {lead}
        </span>
      ) : null}
      {field}
      {trailingIcon ? (
        <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-subtle-foreground [&_svg]:size-4">
          {trailingIcon}
        </span>
      ) : null}
    </div>
  );
});
